import Formatter from './Formatter'
import QueryBuilder from './QueryBuilder'
import axios from '@/plugins/axios'
import _ from 'lodash'
import moment from 'moment'
import getset from './utils/getset'

var formatter = new Formatter()
export default class Model {
    constructor(data = null) {
        // Configurar propriedades reativas
        this._setupReactiveProperties()

        // Configurações específicas do ORM
        this.queryBuilder = new QueryBuilder()
        this.selfValidate()
        this._type = this.resourceName()
        this.links = {}

        // Se dados foram passados, inicializar
        if (data && typeof data === 'object') {
            this._initializeFromData(data)
        }
    }

    _initializeFromData(data) {
        _.forOwn(data, (value, key) => {
            try {
                this[key] = value
            } catch (error) {
                // Se falhar ao setar, usar propriedade privada diretamente
                this[`_${key}`] = value
            }
        })
    }

    _setupReactiveProperties() {
        const casts = this.casts()
        const allCasts = {}

        // Criar cast para TODOS os fields (com ou sem cast definido)
        _.forEach(this.fields(), field => {
            // Se tem cast definido, usa ele. Senão, usa 'any' (sem tratamento)
            allCasts[field] = casts[field] || 'any'
        })

        // Adicionar casts extras que não estão em fields()
        _.forOwn(casts, (cast, field) => {
            if (!allCasts[field]) {
                allCasts[field] = cast
            }
        })

        // Se não tem nenhum campo, retornar
        if (Object.keys(allCasts).length === 0) {
            return
        }

        // Inicializar propriedades privadas para cada campo
        _.forOwn(allCasts, (cast, field) => {
            this[`_${field}`] = null
        })

        // Configurar getset para TODOS os campos
        try {
            getset(this, allCasts)
        } catch (error) {
            console.error('Error setting up reactive properties:', error)
        }
    }

    baseUrl() {
        return process.env.VUE_APP_API_BASE_URL
    }

    request(config) {
        return axios.request(config)
    }

    fields() {
        return []
    }

    casts() {
        return {}
    }

    dates() {
        return []
    }

    relationships() {
        return {}
    }

    computed() {
        return {}
    }

    resourceName() {
        return null
    }

    appends() {
        return []
    }

    aliases() {
        return {}
    }

    // fetch requests

    async makeFetchRequest(url) {
        const requestConfig = {
            method: 'GET',
            url,
            headers: {
                Accept: 'application/vnd.api+json',
            },
        }
        this.queryBuilder.reset()
        let response = await this.request(requestConfig)

        return this.respond(response.data)
    }

    get() {
        return this.makeFetchRequest(
            `${this.resourceUrl()}${this.queryBuilder.getQuery()}`,
        )
    }

    find(id) {
        return this.makeFetchRequest(
            `${this.resourceUrl()}${id}${this.queryBuilder.getQuery()}`,
        )
    }

    all() {
        return this.get()
    }

    paginate(perPage = 10, page = 1) {
        this.queryBuilder.paginate(perPage, page)

        return this.makeFetchRequest(
            `${this.resourceUrl()}${this.queryBuilder.getQuery()}`,
        )
    }

    async fetchRelation(relationship, links = {}) {
        if (_.isUndefined(links.self)) {
            links.self = this.getRelationshipUrl(relationship)
        }

        this[relationship] = await this.relationships()[
            relationship
        ].makeFetchRequest(links.self).data

        return this[relationship]
    }

    // persist requests

    async makePersistRequest(config) {
        config.headers = {
            'Content-Type': 'application/vnd.api+json',
            Accept: 'application/vnd.api+json',
        }

        return this.request(config).then(response =>
            response ? this.respond(response.data) : false,
        )
    }

    save() {
        if (
            Object.prototype.hasOwnProperty.call(this, 'id') &&
            this.id !== null
        ) {
            return this.update()
        }

        return this.create()
    }

    create() {
        return this.makePersistRequest({
            url: this.resourceUrl(),
            method: 'POST',
            data: this.data(),
        })
    }

    update() {
        return this.makePersistRequest({
            url: this.getSelfUrl(),
            method: 'PUT',
            data: this.data(),
        })
    }

    delete() {
        return this.makePersistRequest({
            url: this.getSelfUrl(),
            method: 'DELETE',
        })
    }

    restore() {
        let data = this.data()
        data.deleted_at = null

        return this.makePersistRequest({
            url: `${this.getSelfUrl()}/restore`,
            method: 'PATCH',
            data: data,
        })
    }

    reorder(items) {
        let data = []
        items.forEach((item, index) => {
            data.push({
                id: item.id,
                order: index,
            })
        })

        return this.makePersistRequest({
            url: `${this.resourceUrl()}reorder`,
            method: 'POST',
            data: data,
        })
    }

    attach(model, data = null) {
        let config = {
            url: `${this.getSelfUrl()}/${model._type}/${model.id}`,
            method: 'POST',
        }

        if (data) {
            config.data = data
        }

        return this.makePersistRequest(config)
    }

    detach(model) {
        return this.makePersistRequest({
            url: `${this.getSelfUrl()}/${model._type}/${model.id}`,
            method: 'DELETE',
        })
    }

    sync(relationship) {
        const data = this.serialize(this.data())

        return this.makePersistRequest({
            url: `${this.getSelfUrl()}/${relationship}`,
            method: 'PUT',
            data: data.data.relationships[relationship],
        })
    }

    // modify query string

    with(resourceName) {
        this.queryBuilder.include(resourceName)

        return this
    }

    orderBy(column, direction = 'asc') {
        this.queryBuilder.orderBy(column, direction)

        return this
    }

    orderByDesc(column) {
        return this.orderBy(column, 'desc')
    }

    where(key, value = null, group = null) {
        this.queryBuilder.where(key, value, group)

        return this
    }

    filter(filter, group = null) {
        return this.where(filter, null, group)
    }

    limit(limit) {
        return this.where('limit', limit)
    }

    offset(offset) {
        return this.where('offset', offset)
    }

    select(fields) {
        if (_.isArray(fields)) {
            const selectFields = _.clone(fields)
            fields = {}
            fields[this.resourceName()] = selectFields
        }

        this.queryBuilder.select(fields)

        return this
    }

    // build model

    respond(response) {
        if (response.data === null) return null

        let data = this.deserialize(response)

        if (this.isCollection(data)) {
            return this.resolveCollection(data)
        }

        return this.resolveItem(data)
    }

    resolveCollection(data) {
        let resolved = {}

        if (Object.prototype.hasOwnProperty.call(data, 'links')) {
            resolved.links = data.links
        }

        if (Object.prototype.hasOwnProperty.call(data, 'meta')) {
            resolved.meta = data.meta
        }

        resolved.data = this.newCollection(
            _.map(data.data, item => {
                return this.resolveItem(item)
            }),
        )

        return resolved
    }

    resolveItem(data) {
        return this.hydrate(data)
    }

    hydrate(data) {
        // Criar novo modelo com prototype correto
        let model = Object.create(Object.getPrototypeOf(this))

        // Inicializar propriedades essenciais
        model.queryBuilder = new QueryBuilder()
        model._type = data._type || data.type
        model.links = data.links || {}

        // Configurar propriedades reativas
        model._setupReactiveProperties()

        model.id = data.id

        if (Object.prototype.hasOwnProperty.call(data, 'relationships')) {
            model.relationshipNames = data.relationships
        }

        _.forEach(model.fields(), field => {
            if (data[field] !== undefined) {
                model[field] = data[field]
            }
        })

        _.forOwn(model.dates(), (format, field) => {
            if (data[field] !== undefined) {
                model[field] = moment(data[field])
            }
        })

        _.forEach(data.relationships, relationship => {
            let relation = model.relationships()[relationship]

            if (_.isUndefined(relation)) {
                throw new Error(
                    `Sarale: Relationship ${relationship} has not been defined in ${model.constructor.name} model.`,
                )
            }

            if (this.isCollection(data[relationship])) {
                model[relationship] = relation.resolveCollection(
                    data[relationship],
                ).data
            } else if (data[relationship].data) {
                model[relationship] = relation.resolveItem(
                    data[relationship].data,
                )
            }
        })

        _.forOwn(model.relationships(), (relatedModel, relationshipName) => {
            if (_.isUndefined(model[relationshipName])) {
                model[relationshipName] = {
                    fetch: () => {
                        return model.fetchRelation(relationshipName)
                    },
                }
            }
        })

        _.forOwn(model.computed(), (computation, key) => {
            model[key] = computation(model)
        })

        return model
    }

    // extract data from model

    data() {
        let data = {}
        data.TYPE = this.TYPE

        if (
            Object.prototype.hasOwnProperty.call(this, 'id') &&
            this.id !== null
        ) {
            data.id = this.id
        }

        _.forEach(this.fields(), field => {
            let fieldValue = this[field]

            // Se for undefined, tentar pela propriedade privada (dados reais)
            if (_.isUndefined(fieldValue)) {
                fieldValue = this[`_${field}`]
            }

            if (!_.isUndefined(fieldValue) && !_.isNull(fieldValue)) {
                data[field] = fieldValue
            }
        })

        _.forEach(this.aliases(), (newValue, actual) => {
            let actualValue = this[actual]

            // Se for undefined, tentar pela propriedade privada
            if (_.isUndefined(actualValue)) {
                actualValue = this[`_${actual}`]
            }

            if (!_.isUndefined(actualValue) && !_.isNull(actualValue)) {
                data[newValue] = actualValue
            }
        })

        _.forOwn(this.dates(), (format, field) => {
            let dateValue = this[field]

            // Se for undefined, tentar pela propriedade privada
            if (_.isUndefined(dateValue)) {
                dateValue = this[`_${field}`]
            }

            if (!_.isUndefined(dateValue) && !_.isNull(dateValue)) {
                //Se a data for valida, envia a data formatada se nao envia null
                data[field] = moment(dateValue).isValid()
                    ? moment(dateValue).format(format)
                    : null
            }
        })

        let appends = this.appends()

        _.forEach(this.relationships(), (model, relationship) => {
            // Se a relação nao foi definida no modelo, simplesmente ignora
            if (
                !_.isUndefined(this[relationship]) &&
                this[relationship] !== null &&
                !appends.includes(relationship)
            ) {
                if (_.isArray(this[relationship])) {
                    data[relationship] = _.map(this[relationship], relation => {
                        // Se é um modelo com método data, usar ele
                        if (relation && typeof relation.data === 'function') {
                            const relationData = relation.data()
                            return relationData
                        }

                        // Se é um modelo sem método data (pode acontecer após clone), usar o próprio objeto
                        if (relation && relation.id) {
                            return relation
                        }

                        return null
                    })
                } else {
                    // Se é um objeto com método data
                    if (typeof this[relationship].data === 'function') {
                        data[relationship] = this[relationship].data()
                    }
                    // Se é um modelo direto (pode acontecer após clone ou propriedade reativa)
                    else if (this[relationship] && this[relationship].id) {
                        data[relationship] = this[relationship]
                    } else {
                        data[relationship] = null
                    }
                }
            }
        })

        _.forEach(appends, field => {
            let appendValue = this[field]

            // Se for undefined, tentar pela propriedade privada
            if (_.isUndefined(appendValue)) {
                appendValue = this[`_${field}`]
            }

            if (!_.isUndefined(appendValue) && !_.isNull(appendValue)) {
                data[field] = appendValue
            }
        })

        return data
    }

    // helpers

    resourceUrl() {
        return `${this.baseUrl()}/${this.resourceName()}/`
    }

    getSelfUrl() {
        this.links.self = `${this.resourceUrl()}${this.id}`
        return this.links.self
    }

    getRelationshipUrl(relationship) {
        return `${this.getSelfUrl()}/relationships/${relationship}`
    }

    isCollection(data) {
        return (
            Object.prototype.hasOwnProperty.call(data, 'data_collection') &&
            data.data_collection === true &&
            _.isArray(data.data)
        )
    }

    deserialize(data) {
        return formatter.deserialize(data, this.aliases())
    }

    serialize(data) {
        return formatter.serialize(data)
    }

    selfValidate() {
        const name = this.resourceName()

        if (name === null || !_.isString(name) || name.length === 0) {
            throw new Error(
                `Sarale: Resource name not defined in ${this.constructor.name} model. Implement resourceName method in the ${this.constructor.name} model to resolve this error.`,
            )
        }
    }

    clone() {
        // Criar novo modelo do mesmo tipo (isso configura getters/setters automaticamente)
        const ClonedModel = this.constructor
        const cloned = new ClonedModel()

        // Copiar ID se existir
        if (
            Object.prototype.hasOwnProperty.call(this, 'id') &&
            this.id !== null
        ) {
            cloned.id = this.id
        }

        // Copiar _type e links
        cloned._type = this._type
        cloned.links = _.cloneDeep(this.links)

        // Copiar todos os fields (usando getters/setters)
        // Ignorar valores undefined e null
        _.forEach(this.fields(), field => {
            if (!_.isUndefined(this[field]) && !_.isNull(this[field])) {
                cloned[field] = _.cloneDeep(this[field])
            }
        })

        // Copiar dates (ignorar null)
        _.forOwn(this.dates(), (format, field) => {
            if (!_.isUndefined(this[field]) && !_.isNull(this[field])) {
                cloned[field] = moment(this[field])
            }
        })

        // Copiar relationships (ignorar null e objetos com fetch)
        _.forOwn(this.relationships(), (model, relationship) => {
            // Primeiro verificar se a propriedade privada existe (propriedade reativa)
            const privateProperty = `_${relationship}`
            const hasPrivateProperty = Object.prototype.hasOwnProperty.call(
                this,
                privateProperty,
            )

            if (
                hasPrivateProperty &&
                !_.isUndefined(this[privateProperty]) &&
                !_.isNull(this[privateProperty])
            ) {
                // Se tem propriedade privada e tem dados, usar ela
                cloned[relationship] = _.cloneDeep(this[privateProperty])
            } else if (
                !_.isUndefined(this[relationship]) &&
                !_.isNull(this[relationship]) &&
                !(
                    typeof this[relationship] === 'object' &&
                    _.isFunction(this[relationship].fetch)
                ) // Não copiar objetos com método fetch (não carregados)
            ) {
                // Se não tem propriedade privada mas tem dados diretos (e não é objeto de fetch)
                cloned[relationship] = _.cloneDeep(this[relationship])
            }
        })

        // Copiar campos de casts que não estão em fields() (usar propriedades privadas se existirem)
        _.forOwn(this.casts(), (cast, field) => {
            if (!this.fields().includes(field)) {
                // Primeiro verificar se a propriedade privada existe (propriedade reativa)
                const privateProperty = `_${field}`
                const hasPrivateProperty = Object.prototype.hasOwnProperty.call(
                    this,
                    privateProperty,
                )

                if (
                    hasPrivateProperty &&
                    !_.isUndefined(this[privateProperty]) &&
                    !_.isNull(this[privateProperty])
                ) {
                    // Se tem propriedade privada e tem dados, usar ela
                    cloned[field] = _.cloneDeep(this[privateProperty])
                } else if (
                    !_.isUndefined(this[field]) &&
                    !_.isNull(this[field])
                ) {
                    // Se não tem propriedade privada mas tem dados diretos
                    cloned[field] = _.cloneDeep(this[field])
                }
            }
        })

        return cloned
    }

    newCollection(data) {
        return data
    }
}
